import * as React from 'react'

import { cn } from '@modules/lib/lib'

/**
 * サムネイル切替タブの統一コンポーネント。
 *
 * AI Color (`AiImageCard`) と Area Estimation (カット切替) で共通利用する SSOT。
 *
 * デザイン仕様 (Figma 13607-16250 + AI Color AiImageCard 準拠):
 *   - サイズ: 144 × 108 px 固定 (`w-[144px] h-[108px]`)
 *   - 背景: `bg-muted` (#F4F4F5)
 *   - 角丸: `rounded-lg` (8px)
 *   - 非選択: `border border-input` (1px グレー)
 *   - 選択: `border-2 border-foreground` (2px 濃いグレー) — AiImageCard 同等
 *   - 内部画像は `object-contain` (AiImageCard 準拠、画像比率維持)
 *
 * 構成:
 *   - button (アクセシビリティ: aria-pressed / aria-label / role はオプションで上書き可)
 *   - children に label/badge 等の overlay を渡す
 */
interface Props {
  imageUrl?: string
  isSelected: boolean
  onSelect?: () => void
  /** デフォルト button (アクセシビリティ用)。aria-label を必ず指定 */
  ariaLabel: string
  /** サムネ右下や左下に重ねる label / badge / アクションボタン等 */
  children?: React.ReactNode
  /** role 上書き (tablist 配下なら 'tab') */
  role?: React.AriaRole
}

export const ThumbnailTab: React.FC<Props> = ({
  imageUrl,
  isSelected,
  onSelect,
  ariaLabel,
  children,
  role,
}) => (
  <div className="relative h-[108px] w-[144px]">
    <button
      aria-label={ariaLabel}
      aria-selected={role === 'tab' ? isSelected : undefined}
      aria-pressed={role !== 'tab' ? isSelected : undefined}
      className={cn(
        'h-full w-full overflow-hidden rounded-lg bg-muted transition',
        isSelected ? 'border-2 border-foreground' : 'border border-input',
      )}
      disabled={!onSelect}
      onClick={onSelect}
      role={role}
      type="button"
    >
      {imageUrl ? <img alt="" className="h-full w-full object-contain" src={imageUrl} /> : null}
    </button>
    {children}
  </div>
)
